import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select, Action } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, mergeMap, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { successOf } from '@zerops/zef/entities';
import { createClientError } from '@zerops/zef/errors';
import { zefGo, ofRoute } from '@zerops/zef/ngrx-router';
import { Project, ProjectAddPayload } from '@zerops/models/project';
import { ProjectEntity, queueProjectImport } from '@zerops/zerops/core/project-base';
import { UserEntity, storeUserDataSuccess } from '@zerops/zerops/core/user-base';
import { selectFastestLocation } from '@zerops/zerops/core/settings-base';
import { RecipeHandoffBaseApi } from './recipe-handoff-base.api';
import {
  consumeRecipeHandoff,
  consumeRecipeHandoffSuccess,
  consumeRecipeHandoffFail,
  RECIPE_HANDOFF_GREENFIELD_TAG,
  RecipeHandoffPendingState,
  setPendingHandoff,
  setPendingRecipe,
  clearPendingHandoff
} from './recipe-handoff-base.action';
import { selectRecipeHandoffPending } from './recipe-handoff-base.selector';
import { toZagentMiniConfig } from './recipe-handoff-base.adapter';

@Injectable()
export class RecipeHandoffBaseEffect {
  // # Deps
  #actions$ = inject(Actions);
  #api = inject(RecipeHandoffBaseApi);
  #store = inject(Store);
  #projectEntity = inject(ProjectEntity);
  #userEntity = inject(UserEntity);

  // # Effects
  onConsume$ = createEffect(() => this.#actions$.pipe(
    ofType(consumeRecipeHandoff),
    switchMap((action) => this.#api.consume$(action.data.id).pipe(
      map((payload) => consumeRecipeHandoffSuccess(payload, action)),
      // Normalize the raw HTTP / parse error into a user-friendly NormalizedError.
      // The global ErrorsEffect picks the message off `meta.zefError.message`
      // and surfaces it as a snack automatically — no manual zefAddError needed.
      catchError(() => of(consumeRecipeHandoffFail(
        createClientError(
          'Recipe handoff expired or already used.',
          'RECIPE_HANDOFF_CONSUME_FAIL',
          410
        ),
        action
      )))
    ))
  ));

  /**
   * On successful consume, fan out to the appropriate downstream action.
   * Two variants:
   *
   *   - Recipe (recipeSlug present): dispatch `queueProjectImport`.
   *     `zagentConfig` rides through the recipe-deploy chain
   *     (`_onNewRecipeSystemImport$` in app.effect) which prefers it
   *     over the lazy `zagentConfigMap`.
   *
   *   - Greenfield (projectName present): dispatch `projectEntity.addOne`
   *     directly with `zcpConfig` in meta. The centralized
   *     `onProjectAddSuccessDeployZcp$` in project-base.effect.ts deploys
   *     the ZCP service via firstClassRecipeImport; our redirect effect
   *     below navigates the user to the project once it's created.
   *     Equivalent to the form-driven path but without the user having
   *     to land on /dashboard/project-add and click Create.
   */
  onConsumeSuccessFanOut$ = createEffect(() => this.#actions$.pipe(
    ofType(consumeRecipeHandoffSuccess),
    switchMap(({ data }) => {
      const projectName = data.projectName;
      if (projectName) {
        return combineLatest([
          this.#userEntity.activeClientUser$.pipe(filter((u) => !!u?.clientId), take(1)),
          this.#store.pipe(select(selectFastestLocation), filter((l): l is NonNullable<typeof l> => !!l), take(1))
        ]).pipe(
          map(([ { clientId }, location ]) => this.#projectEntity.addOne(
            {
              name: projectName,
              description: '',
              tagList: [],
              location: location.id,
              clientId,
              mode: 'LIGHT',
              // Form-submit path also passes null here; the API accepts it
              // to mean "no limit" even though Project types it as number.
              maxCreditLimit: null,
              userRoles: []
            } as unknown as ProjectAddPayload,
            {
              tag: RECIPE_HANDOFF_GREENFIELD_TAG,
              zcpConfig: toZagentMiniConfig(data)
            }
          ))
        );
      }
      // GitHub control-plane handoff: rebuild a github-shaped import payload so
      // app.effect's _onNewRecipeSystemImport$ takes its github branch (gated on
      // recipeSlug && environmentName && type==='github' && githubUrl). recipeSlug
      // = btoa(githubUrl) matches NonAuthRecipeSelection's github convention
      // (recipe.id = btoa(url)) and only needs to be truthy for that gate; the
      // real lookup uses githubUrl. Native (no githubUrl) keeps the recipeSlug shape.
      const importPayload: Record<string, unknown> = data.githubUrl
        ? {
            type: 'github',
            recipeSlug: btoa(data.githubUrl),
            githubUrl: data.githubUrl,
            environmentName: data.environmentName,
            ...(data.recipeSourceUrl !== undefined ? { recipeSourceUrl: data.recipeSourceUrl } : {})
          }
        : {
            recipeSlug: data.recipeSlug,
            environmentName: data.environmentName,
            ...(data.recipeSourceUrl !== undefined ? { recipeSourceUrl: data.recipeSourceUrl } : {})
          };
      return of(queueProjectImport({
        nonHaRecipeId: JSON.stringify(importPayload),
        zagentConfig: toZagentMiniConfig(data) ?? undefined
      }));
    })
  ));

  /**
   * Redirect to the new project once the greenfield handoff addOne
   * resolves. Self-contained here because this effect (core) is loaded
   * eagerly, whereas DashboardProjectAddEffect's redirect is only
   * registered while the dashboard module is active.
   */
  onGreenfieldAddProjectSuccessRedirect$ = createEffect(() => this.#actions$.pipe(
    successOf<Project>(this.#projectEntity.addOne),
    filter(({ originalAction }) => originalAction?.meta?.tag === RECIPE_HANDOFF_GREENFIELD_TAG),
    map(({ meta, originalAction }) => ({ id: meta?.rawResult?.id, originalAction })),
    filter((v): v is { id: string; originalAction: typeof v.originalAction } => !!v.id),
    map(({ id, originalAction }) => {
      const hasZcp = !!originalAction?.meta?.zcpConfig;
      return zefGo([ '/project', id, hasZcp ? 'service-stacks' : 'add-service-stack' ]);
    })
  ));

  /**
   * Capture a pending handoff the moment the user lands on the login or
   * registration route carrying one. Auth-method-agnostic: the whole
   * password/2FA/passkey ceremony stays on /login, so this is the single
   * reliable capture point for the methods whose success actions carry no
   * meta (passkey/2FA). First-writer-wins is enforced by the reducer.
   * `?handoff=` (T1, raw uuid) wins over `?recipe=` (T2, raw JSON) to match
   * the previous precedence.
   */
  onAuthRouteStashPending$ = createEffect(() => this.#actions$.pipe(
    ofRoute<{ handoff?: string; recipe?: string }>([ 'login', 'registration' ]),
    map(({ payload: { routerState: { queryParams } } }) => {
      if (queryParams?.handoff) { return setPendingHandoff(queryParams.handoff); }
      if (queryParams?.recipe) { return setPendingRecipe(queryParams.recipe); }
      return null;
    }),
    filter((a): a is NonNullable<typeof a> => !!a)
  ));

  /**
   * Drain the stash exactly once, when the session is fully authenticated and
   * org-resolved. Gates:
   *  - refresh !== true: refresh===true is an org-switch hard reload.
   *  - clientUserId truthy: a missing clientUserId is a zero-active-account
   *    login (NO_ACTIVE_ACCOUNTS) that can't import — draining there would
   *    waste the single-use token, and it also keeps an orphan stash (from an
   *    already-authed user manually hitting /login) from draining against an
   *    unrelated session.
   * Cannot fire on a partial 2FA token (loadUser is gated on !requires2FA
   * upstream, so no storeUserData is dispatched there). distinctUntilChanged on
   * the id collapses any same-id burst (a duplicate storeUserDataSuccess in one
   * cycle, before the clear reduces) so the single-use consume fires exactly
   * once independent of dispatch/reduce timing, while still re-arming for a
   * genuinely new id.
   */
  onFullyAuthedConsumePending$ = createEffect(() => this.#actions$.pipe(
    ofType(storeUserDataSuccess),
    filter(({ refresh, clientUserId }) => refresh !== true && !!clientUserId),
    withLatestFrom(this.#store.pipe(select(selectRecipeHandoffPending))),
    map(([ , pending ]) => pending),
    filter((pending): pending is RecipeHandoffPendingState => !!pending?.id),
    distinctUntilChanged((a, b) => a.id === b.id),
    mergeMap((pending) => [
      this.#pendingToConsumeAction(pending),
      clearPendingHandoff()
    ])
  ));

  /**
   * Map a stashed pending entry to its drain action. A `?handoff=` token is
   * consumed directly. A `?recipe=` entry is the raw producer JSON forwarded
   * verbatim into queueProjectImport, whose existing owner
   * (_onQueueProjectImport$ in app.effect.ts) does the single handoff-vs-plain-
   * recipe branching — so the handoff extraction lives in exactly one place.
   */
  #pendingToConsumeAction(pending: RecipeHandoffPendingState): Action {
    const id = pending.id ?? '';
    return pending.source === 'handoff'
      ? consumeRecipeHandoff({ id })
      : queueProjectImport({ nonHaRecipeId: id });
  }
}
